import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Gift, LogOut, MessageCircle, Send, UserCircle2, Wallet2 } from "lucide-react";
import { api, formatApiError } from "../../api";
import { useMemberData } from "../../contexts/MemberDataContext";
import { MEMBER } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { toast } from "../../components/ui/sonner";

const TOGGLES = [
  { channel: "whatsapp", purpose: "marketing", label: "WhatsApp — offers & promos" },
  { channel: "whatsapp", purpose: "reminders", label: "WhatsApp — class/session reminders" },
  { channel: "email", purpose: "marketing", label: "Email — offers & promos" },
  { channel: "email", purpose: "reminders", label: "Email — class/session reminders" },
];

// UX-MEM-01 §1 SCR-P03-07 highest-risk rule ("prove opting out actually blocks a send"): the
// toggle grid writes to the REAL `consent` collection (`routers/comms.py`'s `set_consent`,
// unmodified) — the Test-send button then calls `comms_engine._check_consent_gate` (the exact
// gate a real broadcast would hit) and reports the true allow/block outcome, not a guess.
export default function More() {
  const { me, logout } = useMemberData();
  const reducedMotion = useReducedMotion();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [referredName, setReferredName] = useState("");
  const [referredPhone, setReferredPhone] = useState("");
  const [testResult, setTestResult] = useState(null);

  const consentQ = useQuery({ queryKey: ["member-consent"], queryFn: async () => (await api.get("/member/consent")).data.consent });
  const consentMap = new Map((consentQ.data || []).map((c) => [`${c.channel}-${c.purpose}`, c.granted]));

  const toggleMutation = useMutation({
    mutationFn: ({ channel, purpose, granted }) => api.post("/member/consent", { channel, purpose, granted }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["member-consent"] }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const testSendMutation = useMutation({
    mutationFn: () => api.post("/member/notifications/test-send"),
    onSuccess: (res) => setTestResult(res.data),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const referralMutation = useMutation({
    mutationFn: () => api.post("/member/referrals", { referred_name: referredName, referred_phone: referredPhone }),
    onSuccess: () => { toast.success("Referral sent — thank you!"); setReferredName(""); setReferredPhone(""); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <div data-testid={MEMBER.morePage} className="space-y-6">
      <motion.div {...fadeUp(reducedMotion)}>
        <p className="font-heading text-2xl font-bold">{me?.name}</p>
        <p className="text-sm" style={{ color: "#A1A1AA" }}>{me?.phone}</p>
      </motion.div>

      <motion.button {...fadeUp(reducedMotion, 0.03)} data-testid={MEMBER.profileLink} onClick={() => navigate("/member/profile")} className="w-full text-left rounded-2xl p-4 flex items-center gap-3 active:scale-95 transition-transform" style={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)" }}>
        <UserCircle2 size={18} color="#D6202B" /> <span className="text-sm font-semibold">My profile</span>
      </motion.button>

      <motion.button {...fadeUp(reducedMotion, 0.045)} data-testid={MEMBER.membershipLink} onClick={() => navigate("/member/membership")} className="w-full text-left rounded-2xl p-4 flex items-center gap-3 active:scale-95 transition-transform" style={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)" }}>
        <Wallet2 size={18} color="#D6202B" /> <span className="text-sm font-semibold">My membership</span>
      </motion.button>

      <motion.div {...fadeUp(reducedMotion, 0.06)} className="rounded-2xl p-5 space-y-4" style={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)" }}>
        <p className="text-sm font-semibold">Notification preferences</p>
        {TOGGLES.map((t) => {
          const granted = consentMap.get(`${t.channel}-${t.purpose}`) || false;
          return (
            <div key={`${t.channel}-${t.purpose}`} className="flex items-center justify-between gap-3">
              <span className="text-sm" style={{ color: "#D4D4D8" }}>{t.label}</span>
              <Switch
                data-testid={MEMBER.preferenceToggle(t.channel, t.purpose)} checked={granted}
                onCheckedChange={(checked) => toggleMutation.mutate({ channel: t.channel, purpose: t.purpose, granted: checked })}
                className="data-[state=checked]:bg-[#D6202B] data-[state=unchecked]:bg-[#3f3f46]"
              />
            </div>
          );
        })}
        <Button data-testid={MEMBER.testSendButton} disabled={testSendMutation.isPending} variant="outline" onClick={() => testSendMutation.mutate()} className="w-full gap-2 border-white/15 text-white bg-transparent hover:bg-white/10">
          <Send size={14} /> Send test WhatsApp notification
        </Button>
        {testResult && (
          <p data-testid={MEMBER.testSendResult} className="text-xs" style={{ color: testResult.whatsapp_allowed ? "#32D74B" : "#D6202B" }}>
            {testResult.whatsapp_allowed ? "Sent — consent allows this channel." : `Blocked (${testResult.block_reason}) — your preference is respected.`}
          </p>
        )}
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.09)} className="rounded-2xl p-5 space-y-3" style={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)" }}>
        <p className="text-sm font-semibold flex items-center gap-2"><Gift size={16} color="#D6202B" /> Refer a friend</p>
        <Input data-testid={MEMBER.referralNameInput} placeholder="Friend's name" value={referredName} onChange={(e) => setReferredName(e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white" />
        <Input data-testid={MEMBER.referralPhoneInput} placeholder="Friend's phone" value={referredPhone} onChange={(e) => setReferredPhone(e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white" />
        <Button data-testid={MEMBER.referralSubmitButton} disabled={!referredName.trim() || !referredPhone.trim() || referralMutation.isPending} onClick={() => referralMutation.mutate()} className="w-full text-white" style={{ background: "#D6202B" }}>Send referral</Button>
      </motion.div>

      <a data-testid={MEMBER.whatsappHelpLink} href="https://wa.me/910000000000" target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-2xl p-4" style={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)" }}>
        <MessageCircle size={18} color="#32D74B" /> <span className="text-sm font-semibold">Chat with your gym on WhatsApp</span>
      </a>

      <Button data-testid={MEMBER.logoutButton} variant="outline" onClick={logout} className="w-full gap-2 border-white/15 text-white bg-transparent hover:bg-white/10">
        <LogOut size={14} /> Log out
      </Button>
    </div>
  );
}
